import { appStateForModalsAtom } from '@/atoms/launchModalsConfigAtom';
import { currentQueuedModalAtom, modalQueueAtom } from '@/atoms/modalQueueAtoms';
import {
  isModalTierFrameworkEnabledAtom,
  modalTierFrameworkDevOverrideAtom,
} from '@/atoms/modalTierFramework';
import { MODAL_TIER_REGISTER } from '@/atoms/modalTierRegister';
import { resolveTierGateContext } from '@/lib/modalTiers';
import { Button } from '@heidi/design-system/button';
import { Switch } from '@heidi/design-system/switch';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { describeQueueTopVerdict } from './modal-tier-framework-verdict';

const boolLabel = (value: boolean): string => (value ? 'yes' : 'no');

type TierStatProps = {
  label: string;
  value: string;
  testId: string;
};

const TierStat = ({ label, value, testId }: TierStatProps) => (
  <div className="flex items-baseline justify-between gap-3">
    <TypographyCaption className="text-text-secondary">{label}</TypographyCaption>
    <TypographyCaption
      className="shrink-0 font-mono text-text-primary"
      data-testid={testId}
    >
      {value}
    </TypographyCaption>
  </div>
);

/**
 * Dev-tools UI for the T0–T5 modal tier framework
 * (`release-growth-modal-tier-framework`). Force the gate on/off locally without
 * PostHog and inspect the live gate verdict for the queue-top modal. Same actions
 * as `__heidiModals.tier()`.
 */
export function ModalTierFrameworkDevSection() {
  const enabled = useAtomValue(isModalTierFrameworkEnabledAtom);
  const override = useAtomValue(modalTierFrameworkDevOverrideAtom);
  const appState = useAtomValue(appStateForModalsAtom);
  const queueTop = useAtomValue(modalQueueAtom).at(0) ?? null;
  const current = useAtomValue(currentQueuedModalAtom)?.id ?? '—';

  const setOverride = useSetAtom(modalTierFrameworkDevOverrideAtom);

  const registeredCount = Object.keys(MODAL_TIER_REGISTER).length;
  const ctx = resolveTierGateContext({
    isClinicalRecordingActive: appState.isClinicalRecordingActive,
    isUserManaged: appState.isUserManaged,
    isUserAtLeastAWeekOld: appState.isUserAtLeastAWeekOld,
  });
  const queueTopVerdict = describeQueueTopVerdict(queueTop, enabled, ctx);

  const handleToggle = useCallback(
    (checked: boolean) => setOverride(checked),
    [setOverride]
  );

  const handleFollowFlag = useCallback(() => setOverride(null), [setOverride]);

  return (
    <section aria-labelledby="dev-tools-modal-tier-heading" className="mb-6">
      <TypographyP2SemiBold id="dev-tools-modal-tier-heading" className="mb-2">
        Modal tier framework
      </TypographyP2SemiBold>
      <TypographyCaption className="mb-3 text-text-tertiary">
        Central R1–R5 tier gate over launch surfaces, flagged by{' '}
        <span className="font-mono">release-growth-modal-tier-framework</span>. Only
        modals present in <span className="font-mono">MODAL_TIER_REGISTER</span> are gated
        — everything else passes through. The override is local to this tab and resets on
        reload.
      </TypographyCaption>

      <div className="mb-3 flex items-center justify-between gap-3 rounded-md border border-border p-3">
        <div className="flex flex-col">
          <TypographyCaption className="text-text-primary">
            Force framework on
          </TypographyCaption>
          <TypographyCaption className="text-text-tertiary">
            {override === null ? 'Following PostHog flag' : 'Dev override active'}
          </TypographyCaption>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={handleToggle}
          aria-label="Force modal tier framework on"
          data-testid="dev-tools-modal-tier-toggle"
        />
      </div>

      <div className="mb-3 flex flex-col gap-1.5 rounded-md border border-border p-3">
        <TierStat
          label="Framework enabled"
          value={boolLabel(enabled)}
          testId="dev-tools-modal-tier-enabled"
        />
        <TierStat
          label="Control source"
          value={override === null ? 'PostHog flag' : 'dev override'}
          testId="dev-tools-modal-tier-source"
        />
        <TierStat
          label="Registered modals"
          value={`${registeredCount}`}
          testId="dev-tools-modal-tier-registered-count"
        />
        <TierStat
          label="Recording active (R1)"
          value={boolLabel(appState.isClinicalRecordingActive)}
          testId="dev-tools-modal-tier-recording"
        />
        <TierStat
          label="Managed / enterprise (R2)"
          value={boolLabel(appState.isUserManaged)}
          testId="dev-tools-modal-tier-enterprise"
        />
        <TierStat
          label="Account ≥ 7 days (R3)"
          value={boolLabel(appState.isUserAtLeastAWeekOld)}
          testId="dev-tools-modal-tier-week-old"
        />
        <TierStat
          label="Queue top"
          value={queueTop?.id ?? '—'}
          testId="dev-tools-modal-tier-queue-top"
        />
        <TierStat
          label="Queue-top verdict"
          value={queueTopVerdict}
          testId="dev-tools-modal-tier-verdict"
        />
        <TierStat
          label="Currently showing"
          value={current}
          testId="dev-tools-modal-tier-current"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleFollowFlag}
          disabled={override === null}
          data-testid="dev-tools-modal-tier-follow-flag"
        >
          Follow flag
        </Button>
      </div>
    </section>
  );
}
